<template>
  <sidebar-row border-bottom class="pt-2">
    <template v-if="lightingSituationFeaturesLoaded">
      <v-row no-gutters>
        <base-button
          translation-path="sidebar.masterplanStreets.manageLightingSituation"
          :disabled="disabled"
          @click="manageLightingSituation"
        />
      </v-row>
      <v-row no-gutters class="pt-3 pb-5">
        <v-col cols="12">
          <ribbon-list
            :listItems="lightingSituationFeatures"
            item-title-key="id"
            style-type="raw"
            disable-paddings
            :toggleable="false"
            fixed-height
          >
            <template #default="{ item }">
              <v-col cols="auto" class="px-1">
                <img :src="item.imageUrl" width="28.5" height="28.5" />
              </v-col>
              <v-col cols="auto" class="text-body-2 pl-1">
                {{ item.text }}
              </v-col>
              <v-spacer />
              <v-col cols="auto" class="pr-3" v-if="item.width">
                <v-tooltip bottom color="font">
                  <template #activator="{ on }">
                    <v-chip label small v-on="on">
                      {{ item.width }}
                    </v-chip>
                  </template>
                  {{ badgesAttributesNames['width'] }}
                </v-tooltip>
              </v-col>
              <v-col cols="auto" class="pr-3" v-if="item.surface">
                <v-tooltip bottom color="font">
                  <template #activator="{ on }">
                    <v-chip label small v-on="on">
                      {{ item.surface }}
                    </v-chip>
                  </template>
                  {{ badgesAttributesNames['surface'] }}
                </v-tooltip>
              </v-col>
              <v-col cols="auto" class="pr-3" v-if="item.badge">
                <v-tooltip bottom color="font">
                  <template #activator="{ on }">
                    <v-chip
                      :color="item.badgeColor"
                      label
                      small
                      :class="{
                        'white--text': !$_isHexColorLight(item.badgeColor),
                        'font--text': $_isHexColorLight(item.badgeColor),
                      }"
                      v-on="on"
                    >
                      {{ item.badge }}
                    </v-chip>
                  </template>
                  {{ badgesAttributesNames['situation'] }}
                </v-tooltip>
              </v-col>
            </template>
          </ribbon-list>
        </v-col>
      </v-row>
      <masterplan-streets-widget-dialog
        v-if="isManageLightingSituationLoaded"
        :is-visible.sync="isManageLightingSituationVisible"
        :fid="fid"
        :lid="lid"
        :street-properties.sync="currentStreetProperties"
        :features="lightingSituationFeatures"
        :features-mapping-function="getMappedLightingSituationFeatures"
        :type-attribute-dict="typeAttributeDict"
        :arrangement-attribute-dict="arrangementAttributeDict"
        @onSave="onSave"
      />
    </template>
    <main-loader v-else class="py-5" />
  </sidebar-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import RibbonList from '@/components/RibbonList';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'MasterplanStreetsWidget',
  components: {
    MainLoader,
    MasterplanStreetsWidgetDialog: () => import('@/components/MasterplanStreetsWidgetDialog'),
    RibbonList,
    SidebarRow,
  },
  props: {
    fid: {
      type: Number,
      required: true,
    },
    lid: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    streetProperties: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    token: get('authentication/token'),
    dicts: get('admin/dictionaries'),
    dataSources: get('layers/metadata'),

    masterplanSituationsDataSourceMapping: get(
      'admin/modulesMapping@masterplan_2_streets@masterplan_situation_datasource'
    ),
    lightingSituationDataSourceName() {
      return this.masterplanSituationsDataSourceMapping?.datasource || '';
    },
    lightingSituationDataSource() {
      return this.dataSources[this.lightingSituationDataSourceName] || undefined;
    },
    lightingSituationAttributes() {
      return this.masterplanSituationsDataSourceMapping?.settings || {};
    },
    badgesAttributesNames() {
      return ['width', 'surface', 'situation'].reduce((total, current) => {
        total[current] =
          this.lightingSituationDataSource?.attributes_schema.attributes.find(
            attribute => attribute.name === this.lightingSituationAttributes[`${current}_attribute`]
          )?.verbose_name || '';
        return total;
      }, {});
    },

    masterplanStreetsDataSourceMapping: get('admin/modulesMapping@masterplan_2_streets@masterplan_street_datasource'),
    lightingStreetDataSourceName() {
      return this.masterplanStreetsDataSourceMapping?.datasource || '';
    },
    lightingStreetDataSource() {
      return this.dataSources[this.lightingStreetDataSourceName] || undefined;
    },
    lightingStreetAttributes() {
      return this.masterplanStreetsDataSourceMapping?.settings || {};
    },

    computedStreetProperties: {
      get() {
        return this.streetProperties;
      },
      set(nV) {
        this.$emit('update:streetProperties', nV);
      },
    },
  },
  data: () => ({
    currentStreetProperties: {},
    lightingSituationFeatures: [],
    lightingSituationFeaturesLoaded: false,

    isManageLightingSituationLoaded: false,
    isManageLightingSituationVisible: false,

    arrangementAttributeDict: undefined,
    situationAttributeDict: undefined,
    typeAttributeDict: undefined,
  }),
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getDicts: call('admin/getDictionaries'),
    manageLightingSituation() {
      this.currentStreetProperties = { ...this.computedStreetProperties };
      this.isManageLightingSituationLoaded = true;
      this.isManageLightingSituationVisible = true;
    },
    async getLightingSituationFeatures({ fid = this.fid, init = false } = {}) {
      if (init) await this.getDicts();
      try {
        if (init) this.lightingSituationFeaturesLoaded = false;
        const { street_id_attribute: streeIdAttribute } = this.lightingSituationAttributes;
        const r = await this.getDataSourceFeatures({
          dataSource: this.lightingSituationDataSourceName,
          filters: {
            $EQUAL: {
              [this.lightingSituationDataSource?.attributes_use_datasource_qualified_names
                ? streeIdAttribute
                : `${this.lightingSituationDataSourceName}.${streeIdAttribute}`]: { value: fid },
            },
          },
        });
        this.lightingSituationFeatures = this.getMappedLightingSituationFeatures(
          JSON.parse(JSON.stringify(r?.features || []))
        );
      } finally {
        this.lightingSituationFeaturesLoaded = true;
      }
    },
    getMappedLightingSituationFeatures(features = []) {
      [
        { type: 'situation', datasource: 'situation' },
        { type: 'type', datasource: 'situation' },
        { type: 'arrangement', datasource: 'street' },
      ].forEach(({ type, datasource }) => {
        if (!this[`${type}AttributeDict`]) {
          const attribute = this[
            `lighting${this.$_capitalize(datasource)}DataSource`
          ]?.attributes_schema.attributes?.find(
            attribute =>
              attribute.name === this[`lighting${this.$_capitalize(datasource)}Attributes`][`${type}_attribute`]
          );
          const attributeDict = this.dicts[attribute?.dict_id];
          this[`${type}AttributeDict`] =
            attributeDict?.values?.reduce((total, current, index) => {
              total[current] = { color: attribute?.dict_colors[index], image: attributeDict?.images?.[index] };
              return total;
            }, {}) || {};
        }
      });
      const typesCount = {};
      return features
        .sort(
          (a, b) =>
            a.properties[this.lightingSituationAttributes.order_attribute] -
            b.properties[this.lightingSituationAttributes.order_attribute]
        )
        .map(feature => {
          const {
            [this.lightingSituationAttributes.situation_attribute]: situation,
            [this.lightingSituationAttributes.type_attribute]: type,
            [this.lightingSituationAttributes.class_attribute]: clas,
            [this.lightingSituationAttributes.calculation_attribute]: calculator,
            [this.lightingSituationAttributes.width_attribute]: width,
            [this.lightingSituationAttributes.surface_attribute]: surface,
          } = feature.properties;
          const calculatorJson = JSON.parse(calculator || null);
          const calculatorClass = calculatorJson?.[situation]?.class;
          if (typesCount[type]) typesCount[type]++;
          else typesCount[type] = 1;
          const img = this.typeAttributeDict[type]?.image;
          const widthAttributeUnit = this.lightingSituationDataSource?.attributes_schema.attributes.find(
            attribute => attribute.name === this.lightingSituationAttributes.width_attribute
          )?.measurement_unit?.symbol;
          return {
            id: feature.id,
            properties: feature.properties,
            text: `${type} ${typesCount[type]}`,
            imageUrl: img ? `${process.env.VUE_APP_API_URL}/dicts_images/${img}?token=${this.token}` : '',
            ...(situation && (calculatorClass || calculatorClass === 0 || clas || clas === 0)
              ? { badge: `${situation}${clas || clas === 0 ? clas : calculatorClass}` }
              : {}),
            badgeColor: this.situationAttributeDict[situation]?.color || this.$_colors['primary'],
            ...(calculatorClass || calculatorClass === 0 ? { calculatorBadge: `${situation}${calculatorClass}` } : {}),
            surface,
            width: width ? `${width}${widthAttributeUnit ? ` ${widthAttributeUnit}` : ''}` : '',
          };
        });
    },
    onSave() {
      this.$root.$emit('refreshTableData');
      this.$emit('refreshFormData');
      this.getLightingSituationFeatures();
    },
  },
  mounted() {
    this.getLightingSituationFeatures({ init: true });
  },
};
</script>
